/* eslint-disable @typescript-eslint/require-await --
 * the Datadog SDK send is synchronous (it batches internally); the methods are
 * async only to satisfy ILogger. */
import { datadogLogs } from '@datadog/browser-logs';
import { getCurrentUser } from '@features/auth';
import { ENV } from '@constants/env';
import { type LogLevel, type LogEventOptions, type ILogger } from '@types';

type DatadogStatus = 'info' | 'warn' | 'error';

// Datadog has no 'success' status; it maps to 'info' and keeps the original
// level in context so it stays searchable.
const statusByLevel: Record<LogLevel, DatadogStatus> = {
  info: 'info',
  warning: 'warn',
  error: 'error',
  success: 'info',
};

export class DatadogLoggerService implements ILogger {
  private getUserContext (): Record<string, unknown> {
    const user = getCurrentUser();
    if (user == null) {
      return {};
    }

    return {
      userId: user.userId,
      email: user.email,
      role: (user.type ?? []).join(', '),
    };
  }

  private sendLog (
    level: LogLevel,
    message: string,
    options: Omit<LogEventOptions, 'context'> & { context?: Record<string, unknown> } = {},
  ): void {
    // The Datadog SDK is never initialized on local (see initDatadog), so
    // logging there would be a silent no-op; skip explicitly.
    if (ENV === 'local') {
      return;
    }

    const { context = {}, source } = options;

    datadogLogs.logger.log(
      message,
      { level, source, actor: { ...this.getUserContext() }, ...context },
      statusByLevel[level],
    );
  }

  public info = async (message: string, options?: LogEventOptions): Promise<void> => {
    this.sendLog('info', message, options);
  };

  public warning = async (message: string, options?: LogEventOptions): Promise<void> => {
    this.sendLog('warning', message, options);
  };

  public error = async (message: string, options?: LogEventOptions): Promise<void> => {
    this.sendLog('error', message, options);
  };

  public success = async (message: string, options?: LogEventOptions): Promise<void> => {
    this.sendLog('success', message, options);
  };
}
